/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { Button, Form, Spinner } from "react-bootstrap";
import moment from "moment";
import DatePicker from "react-date-picker";
import { ReactComponent as CalendarIcon } from '../../../assets/icons/calendar.svg';
import { Document, Page, pdfjs } from "react-pdf";
import { AuthenticationContext } from "services/context/authentication.context";
import { dateFormat, isoDateFormat } from "common/dateFormatterConfig";
import { blobToDataURL, blobToObjectURL } from "common/file-util";
import { FormContentBlock } from "components/common/FormContentBlock";
import { ChargingAgreement } from "models/charging/agreement";
import { NewChargingAgreementServiceCard } from "./NewChargingAgreementServiceCard";
import { NewChargingAgreementDeviceCard } from "./NewChargingAgreementDeviceCard";
import { NewChargingAgreementCompensationCard } from "./NewChargingAgreementCompensationCard";
import { MeteringPointFormTable } from "components/electricity/NewElectricityAgreement/NewElectricityAgreementSummary";
import { ChargingAgreementForm } from "pages/NewChargingAgreement";
import NewChargingAgreementCustomerForm from "./NewChargingAgreementCustomerForm";
import ChargingAgreementService from "services/charging/charging-agreement.service";
import PDFPreviewModal from "components/Modals/PDFPreviewModal/PDFPreviewModal";
import printJS from "print-js";
import TermsAndConditionsService from "services/terms-and-conditions.service";

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface NewChargingAgreementSummaryProps { 
    form: ChargingAgreementForm, 
    setForm: any, 
    agreement: ChargingAgreement
    isCompany: boolean, 
    toggleTerms: () => void,
    termsAccepted: boolean
 }

export function NewChargingAgreementSummary({ 
    form, 
    setForm, 
    isCompany,
    toggleTerms, 
    termsAccepted,
    agreement
}: NewChargingAgreementSummaryProps
) {
    const [numPages, setNumPages] = useState<number | undefined>();
    const [termsPdfPreview, setTermsPdfPreview] = useState<string | undefined>();
    const [termsPdfLoaded, setTermsPdfLoaded] = useState(false);
    const [clientWidth, setClientWidth] = useState(window.innerWidth);
    const [pdfPreview, setPdfPreview] = useState<string | undefined>();
    const [pdfPreviewLoading, setPdfPreviewLoading] = useState(false);
    const [pdfPreviewIsDownloading, setPdfPreviewIsDownloading] = useState(false);
    
    const { state } = useContext(AuthenticationContext);

    const getPdfPreview = async () => {
        try {
            setPdfPreviewLoading(true);

            const blob = await ChargingAgreementService.getAgreementPreviewFile(
                agreement.id,
                {
                    customerId: state.customerId!,
                    startDate: moment(form.startDate!).format(isoDateFormat), 
                    email: form.email!, 
                    phone: form.phone!, 
                    address: form.address!
                }
            );
                
            const objectUrl = blobToObjectURL(blob);
            setPdfPreview(objectUrl);
        } catch (e) {
            console.error(e)
        } finally {
            setPdfPreviewLoading(false);
        }
    }

    const downloadPdfPreview = async () => {
        try {
            setPdfPreviewIsDownloading(true)      

            await ChargingAgreementService.getAgreementPreviewFileWithDownload(
                agreement.id,
                `${agreement.contractNumber}.pdf`,
                {
                    customerId: state.customerId!,
                    startDate: moment(form.startDate!).format(isoDateFormat), 
                    email: form.email!, 
                    phone: form.phone!, 
                    address: form.address!
                }
            );
        } catch(e) {
            console.error(e)
        } finally {
            setPdfPreviewIsDownloading(false)
        }
    }

    useEffect(() => {
        getTermsPdfPreview();
    }, []);

    const getTermsPdfPreview = async () => {
        const blob = await TermsAndConditionsService.getTermsAndConditionsFile(agreement.termsAndConditions.id, isCompany);

        blobToDataURL(blob).then(res => {
            setTermsPdfPreview(res);
        })
    }

    function onDocumentLoadSuccess({ numPages } : {numPages: number}) {
        setNumPages(numPages);
        setTermsPdfLoaded(true);
    }

    function getPageScaleFactor(){
        return clientWidth > 1600 ? 1.5 : 1;
    }

    useEffect(() => {
        const updateWindowDimensions = () => {
          const newWidth = window.innerWidth;
          setClientWidth(newWidth);
        };
    
        window.addEventListener('resize', updateWindowDimensions);
    
        return () => window.removeEventListener('resize', updateWindowDimensions);
    
    }, []);

    const scaleFactor = getPageScaleFactor();

    return (
        <div className="new-electricity-agreement__container">
            <h1>Vaadake üle andmed</h1>
            <FormContentBlock title="Klient">
                <NewChargingAgreementCustomerForm
                    form={form}
                    setForm={setForm}
                    isCompany={isCompany}
                    isFormDisabled={false}
                    showConsolidatedInvoiceCheckbox={false}
                />
            </FormContentBlock>
            <FormContentBlock title="Teenus">
                <NewChargingAgreementServiceCard
                  agreement={state.viewChargingAgreementOffer!}
                />
            </FormContentBlock>
            <FormContentBlock title="Seade">
              <NewChargingAgreementDeviceCard
                pricing={state.viewChargingAgreementOffer?.pricing!}
                address={state.viewChargingAgreementOffer?.address!}
                purchaseOption={state.viewChargingAgreementOffer?.purchaseOption!}
              />
            </FormContentBlock>
            {
                state.viewChargingAgreementOffer?.thirdPartyCompensationOption && (
                    <FormContentBlock title="Kompensatsioon">
                        <NewChargingAgreementCompensationCard
                            pricing={state.viewChargingAgreementOffer?.pricing!}
                            thirdPartyCompensationOption={state.viewChargingAgreementOffer?.thirdPartyCompensationOption!}
                        />
                    </FormContentBlock>
                )
            }
            <FormContentBlock title="Tarbimiskoht">
                <MeteringPointFormTable 
                    meteringPoints={[agreement.meteringPoint]} 
                    withResale={false}
                    isOffer={!!state.viewChargingAgreementOffer}
                />
            </FormContentBlock>
            <FormContentBlock title="Leping">
                <div className="d-lg-flex justify-content-between align-items-start">
                    <Form.Group controlId={'form-date'} className='d-flex flex-column'>
                        <Form.Label>
                            <span className='text-nowrap'>{'Lepingu algus'}</span>
                        </Form.Label>
                        <DatePicker
                            className={form.startDate === undefined && form.validated ? 'invalid' : ''}
                            value={form.startDate}
                            locale='et'
                            clearIcon={null}
                            calendarIcon={<CalendarIcon />}
                            format='dd.MM.yyyy'
                            minDate={new Date()}
                            maxDate={agreement.endDate ? new Date(agreement.endDate) : undefined}
                            onChange={(event: any) => {
                                setForm({
                                    ...form, 
                                    startDate: event ? new Date(event) : undefined
                                })
                            }}
                        />
                        {
                            form.startDate === undefined && form.validated &&
                            <div className="d-block text-right invalid-feedback">Kohustuslik väli</div>
                        }
                        {
                            agreement.endDate ? (
                                <>
                                    <Form.Label className="mt-2">
                                        <span className='text-nowrap'>Lepingu lõpp</span>
                                    </Form.Label>
                                    <span className="u-fs-16">
                                        {moment(new Date(agreement.endDate)).format(dateFormat)}
                                    </span>
                                </>
                            ) : null
                        }
                    </Form.Group>
                    <Button 
                        variant="outline-primary" 
                        size="lg" 
                        onClick={getPdfPreview}
                        disabled={pdfPreviewLoading}
                        className="stretchable-mobile-button mt-2 mt-lg-0"
                    >
                        Vaata lepingut{ false ? <Spinner size={"sm"} animation={"border"} className="ml-1"/> : null }
                    </Button>
                </div>
            </FormContentBlock>                

            <FormContentBlock title="Tingimused">
                <div className="new-electricity-agreement__summary__terms-pdf-container mt-1 mb-5">
                    { termsPdfLoaded ? null : <Spinner animation="grow" variant="primary" className="my-5" />}
                    <Document 
                        className="mx-n4 mx-lg-0" 
                        loading='' 
                        noData='' 
                        file={termsPdfPreview}
                        onLoadError={console.error} 
                        onLoadSuccess={onDocumentLoadSuccess}
                    >
                        {
                            Array(numPages).fill(0).map((_, i) => <Page key={i + ''} scale={scaleFactor}  pageNumber={i + 1}/>)
                        }
                    </Document>
                </div>
                <div className="col-12">
                    <Form.Check
                        id='check-terms'
                        custom
                        type="checkbox"
                        label={state.viewChargingAgreementOffer?.termsAndConditions.confirmationText}
                        className='checkbox-large mt-3'
                        onChange={toggleTerms}
                        checked={termsAccepted}
                    />                      
                </div>
            </FormContentBlock>
            <PDFPreviewModal
                show={!!pdfPreview}
                onHide={() => setPdfPreview(undefined)}
                onDownload={downloadPdfPreview}
                isDownloading={pdfPreviewIsDownloading}
                onPrint={() => printJS({printable: pdfPreview!, type: 'pdf'})}
                pdfPreview={pdfPreview}
            />
        </div>
    );
}